/** RowsGenerator
 * The purpose of this component is to do the following:
 * -- Return groups of horizontal content rows
 * -- Retrieve card data for each row
 * -- Determine what kind of row is appropriate to render based on context and content
 * --
 */
import React from "react";

import { LoadingSpinner } from "@newsela/angelou";
import { isEmpty, isUndefined } from "@s-libs/micro-dash";
import PropTypes from "prop-types";

import ClipRow from "@/browse/ClipRow";
import RowIntersection from "@/browse/RowIntersection";
import useContent from "@/content/useContent";

const MEMBER_LIMIT = 4;

const propTypes = {
  /**
   * String used to make requests for row data. Derived from Outline response.
   */
  contentId: PropTypes.string,
  /**
   * Callback function for intersection observer
   */
  onIntersect: PropTypes.func,
  /**
   * Integer/numeric value representing Row position in the context of a page
   */
  rowIndex: PropTypes.number,
  /**
   * The row title in a string
   */
  rowTitle: PropTypes.string,
  /**
   * Array with topic members
   */
  topicMembers: PropTypes.array,
};
/** Evaluate if Row's should link to single or multi topic pages */
const isSeeMoreURLMulti = (topicMembers) => {
  /** First we map through the topicMembers array and fetch the member_count of
   * each topic member */
  const rowMembersCounts = topicMembers.map((rowContent) => {
    return rowContent.member_count;
  });
  /** Then, if we discover some members have non-zero values we retrun a true
   * value and link to a Multi topic page  */
  if (rowMembersCounts.some((memberCounts) => memberCounts > 0)) return true;
  return false;
};

const RowsGenerator = (props) => {
  const { contentId, onIntersect, rowIndex, rowTitle, topicMembers } = props;

  // Row constant provides data to render individual cards
  const {
    data: row,
    error,
    loading,
  } = useContent(contentId, {
    depth: 1,
    member_limit: MEMBER_LIMIT,
    shape: "card",
    use_imgix: true,
  });

  // Renders an individual row array elements as a react components
  const renderRow = (row, rowIndex) => {
    const rowMembers = row.members;
    if (isEmpty(rowMembers)) return null;

    const { content_id } = row;

    return (
      <RowIntersection
        onIntersect={onIntersect}
        slug={row?.slug}
        rowIndex={rowIndex}
        rowTitle={rowTitle}
      >
        <ClipRow
          /* eslint-disable camelcase */
          key={`${content_id}-row`}
          rowData={row}
          seeMoreMultiOrSingle={isSeeMoreURLMulti(topicMembers)}
        />
      </RowIntersection>
    );
  };

  // Loading spinner for first row
  if (loading && rowIndex === 0) return <LoadingSpinner />;
  if (loading && rowIndex >= 1) return <></>;
  // If there's an error at the row level - fail silently
  if (error) return <></>;
  // Render Rows if we pass the above
  if (!loading && isUndefined(error)) return renderRow(row, rowIndex);
};

RowsGenerator.propTypes = propTypes;
export default RowsGenerator;
